import { ENV } from '@/lib/config/env';

export type OAuthPopupResult = { status: 'received'; state: string } | 'error' | 'closed';

// eslint-disable-next-line i18next/no-literal-string -- `window.open` feature string
const POPUP_FEATURES = 'popup,width=520,height=680';
const CLOSE_POLL_MS = 400;

/**
 * Opens the OAuth authorize URL in a popup and resolves when the flow's redirect leg finishes:
 * - `{ status: 'received', state }` — the callback parked the authorization response server-side;
 *   the caller must now complete the exchange through the authenticated endpoint, passing `state`.
 *   Nothing is connected yet at this point.
 * - `'error'` from the callback page's `postMessage` (accepted only from the API origin, where the
 *   callback is served), or
 * - `'closed'` if the user dismisses the popup before it messages (fallback via polling).
 *
 * The message carries no secret — the authorization code stays server-side, and `state` is already
 * in the popup's own URL. Origin-checking is defense in depth: a spoofed message could at worst
 * trigger a completion call that fails against a state the API never issued.
 */
export const openOAuthPopup = (authorizeUrl: string): Promise<OAuthPopupResult> => {
  const apiOrigin = new URL(ENV.INFERENCE_ENGINE_API_BASE_URL).origin;
  const popup = window.open(authorizeUrl, 'mcp-oauth', POPUP_FEATURES);
  // Blocked by a popup blocker, or the browser refused to open it.
  if (!popup) return Promise.resolve('error');

  return new Promise<OAuthPopupResult>((resolve) => {
    let settled = false;

    const finish = (result: OAuthPopupResult) => {
      if (settled) return;
      settled = true;
      window.removeEventListener('message', onMessage);
      clearInterval(closePoll);
      resolve(result);
    };

    const onMessage = (event: MessageEvent) => {
      if (event.origin !== apiOrigin) return;
      const data = event.data as { type?: unknown; status?: unknown; state?: unknown } | null;
      if (data?.type !== 'mcp-oauth') return;
      finish(
        data.status === 'received' && typeof data.state === 'string'
          ? { status: 'received', state: data.state }
          : 'error',
      );
    };
    window.addEventListener('message', onMessage);

    // The callback page closes itself right after messaging; this also catches the user closing it
    // manually (no message ever arrives).
    const closePoll = setInterval(() => {
      if (popup.closed) finish('closed');
    }, CLOSE_POLL_MS);
  });
};
